
import { Order } from "@/utils/peppol/order";
import { roundDecimalsAdvanced } from "@/utils/numbers";
import Vue from "vue";
import AppParty from "./OrderPreviewParty.vue";
import AppLine from "./OrderPreviewLine.vue";
import AppVatGroup from "./OrderPreviewVatGroup.vue";

export default Vue.extend({
	props: {
		order: {
			type: Object as ()=>Order,
			required: true
		}
	},
	computed: {
		bankAccountIban(): string {
			return this.order.BankAccount?.Iban || this.order.Supplier?.IBAN || "";
		},
		bankAccountBic(): string {
			return this.order.BankAccount?.Bic || this.order.Supplier?.BIC || "";
		},
		hasPaymentInfo(): boolean {
			return !!(this.order.PaymentReference || this.bankAccountIban || this.bankAccountBic);
		},
		hasExpiryDate(): boolean {
			if (!this.order.ExpiryDate) return false;
			return this.order.ExpiryDate.getFullYear() > 1;
		},
		hasCashDiscount(): boolean {
			return !!(this.order.CashDiscountPercent && this.order.CashDiscountDays);
		},
		cashDiscountDueDate(): Date | null {
			if (!this.order.CashDiscountDays || !this.order.OrderDate) return null;
			const dueDate = new Date(this.order.OrderDate);
			dueDate.setDate(dueDate.getDate() + this.order.CashDiscountDays);
			return dueDate;
		}
	},
	methods: {
		close(){
			this.$emit("close");
		},
		formatDate(date: Date): string {
			if (!date || isNaN(date.getTime())) return "-";
			return date.toLocaleDateString("nl-BE");
		},
		formatNumber(value: number): string {
			return roundDecimalsAdvanced(value, {
				decimals: 2,
				decimalSeperator: ",",
				thousandsSeperator: ".",
				thousandSeperatorInterval: 3
			});
		},
		// eslint-disable-next-line max-lines-per-function
		printOrder(){
			const printContent = (this.$el as HTMLElement).querySelector(".w-card-content");
			if (!printContent) return;

			const printWindow = window.open("", "_blank");
			if (!printWindow) return;

			printWindow.document.write(`
				<!DOCTYPE html>
				<html>
				<head>
					<title>${this.$ct("order")} ${this.order.OrderNumber}</title>
					<style>
						@page { size: A4 portrait; margin: 15mm; }
						body { font-family: Arial, sans-serif; padding: 20px; }
						.parties { display: flex; gap: 40px; margin-bottom: 24px; }
						.parties > div { flex: 1; }
						.party-label { font-size: 12px; font-weight: 600; text-transform: uppercase; color: #666; margin-bottom: 8px; }
						.party { background: #f9f9f9; padding: 12px 16px; border-radius: 6px; border: 1px solid #e8e8e8; line-height: 1.6; }
						.party-name { font-weight: 600; margin-bottom: 4px; }
						.party-vat { margin-top: 4px; font-size: 12px; color: #666; }
						.dates-row { display: flex; gap: 40px; margin-bottom: 24px; background: #f9f9f9; padding: 12px 16px; border-radius: 6px; border: 1px solid #e8e8e8; }
						.date-item { display: flex; flex-direction: column; }
						.date-label { font-size: 12px; font-weight: 600; text-transform: uppercase; color: #666; margin-bottom: 4px; }
						.date-value { font-size: 14px; font-weight: 500; color: #333; }
						.section-title { font-size: 14px; font-weight: 600; margin: 24px 0 12px 0; padding-bottom: 8px; border-bottom: 2px solid #e0e0e0; }
						table { width: 100%; border-collapse: collapse; font-size: 13px; }
						th { background: #f5f5f5; padding: 10px 12px; font-weight: 600; border-bottom: 1px solid #ddd; }
						td { padding: 10px 12px; border-bottom: 1px solid #eee; }
						.text-right { text-align: right; }
						.text-left { text-align: left; }
						.grand-total { background: #f0f7ff; font-weight: 600; }
						.grand-total td { border-top: 2px solid #1976d2; color: #1976d2; }
						.payment-info { background: #f0f7ff; border: 1px solid #1976d2; border-radius: 6px; padding: 16px; margin-top: 12px; }
						.payment-row { display: flex; justify-content: space-between; padding: 6px 0; }
						.payment-row:not(:last-child) { border-bottom: 1px solid #d0e3f7; }
						.payment-label { font-weight: 600; color: #1976d2; }
						.payment-value { font-family: monospace; }
						.cash-discount-info { background: #fff8e1; border: 1px solid #ffc107; border-radius: 6px; padding: 16px; margin-top: 12px; }
						.cash-discount-row { display: flex; justify-content: space-between; padding: 6px 0; }
						.cash-discount-row:not(:last-child) { border-bottom: 1px solid #ffe082; }
						.cash-discount-label { font-weight: 600; color: #f57c00; }
						.cash-discount-value { font-weight: 500; color: #333; }
						.no-print { display: none; }
						@media print { body { padding: 0; } }
					</style>
				</head>
				<body>
					<h1>${this.$ct("order")} ${this.order.OrderNumber}</h1>
					${printContent.innerHTML}
				</body>
				</html>
			`);
			printWindow.document.close();
			printWindow.focus();
			setTimeout(() => {
				printWindow.print();
				printWindow.close();
			}, 250);
		}
	},
	components: {
		AppParty,
		AppLine,
		AppVatGroup
	},
	i18nEx: {
		componentPrefix: "components.OrderPreview"
	}
});
